<script setup lang="ts">
import { computed, PropType } from "vue";

interface DonutDataItem {
  name: string;
  value: number;
  color?: string;
}

const props = defineProps({
  data: {
    type: Array as PropType<DonutDataItem[]>,
    required: true,
  },
  colors: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  labelPosition: {
    type: String as PropType<"inside" | "outside" | "center" | "none">,
    default: "outside",
  },
  showPercentage: {
    type: Boolean,
    default: true,
  },
});

const total = computed(() =>
  props.data.reduce((sum, item) => sum + item.value, 0),
);

const seriesData = computed(() =>
  props.data.map((item, index) => ({
    name: item.name,
    value: item.value,
    itemStyle: {
      color: item.color || props.colors[index] || undefined,
    },
  })),
);

const labelOption = computed(() => {
  if (props.labelPosition === "none") {
    return { show: false };
  }

  return {
    show: true,
    position: props.labelPosition,
    formatter: (params: any) => {
      if (props.showPercentage) {
        const percentage = ((params.value / total.value) * 100).toFixed(1);
        return `${params.name}\n${percentage}%`;
      }
      return params.name;
    },
    fontWeight: props.labelPosition === "center" ? "bold" : "normal",
    fontSize: props.labelPosition === "center" ? 14 : 12,
  };
});

const option = computed(() => ({
  //When hover the item
  tooltip: {
    trigger: "item",
    formatter: (params: any) => {
      const percentage = ((params.value / total.value) * 100).toFixed(1);
      return `${params.name}: ${params.value} (${percentage}%)`;
    },
  },
  legend: props.labelPosition === "none" ? { bottom: 0 } : undefined,
  series: [
    {
      type: "pie",
      radius: ["45%", "70%"],
      avoidLabelOverlap: true,
      itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
      label: labelOption.value,
      labelLine: {
        show: props.labelPosition === "outside",
      },
      data: seriesData.value,
    },
  ],
}));
</script>

<template>
  <VChart :option="option" autoresize />
</template>
